<template>
  <div class="subscribe__banner">
    <nav class="breadcrumb container" role="navigation" aria-label="Хлібні крихти">
      <a href="https://myglo.com.ua" title="Home">{{t('breadcrumb_store_label')}}</a>
      <span aria-hidden="true">⁄</span>
      <span>{{ t('breadcrumb_page_label') }}</span>
    </nav>

    <div class="container">
      <div class="subscribe__banner-content">
        <h1 class="subscribe__banner-header h2" role="heading" aria-level="1">
          {{ t('header') }}
        </h1>
        <div class="subscribe__banner-description" v-if="hasSubscription" >{{ t('has_subscription_description') }}</div>
        <div class="subscribe__banner-description" v-else>{{ t('description') }}</div>
      </div>
      <div
        class="subscribe__banner-wrapper swiper"
        ref="swiperEl"
        :class="{'blur': props.customer.isVerified === 'false'}"
        :inert="props.customer.isVerified === 'false'"
      >
        <div class="swiper-wrapper">
          <div class="swiper-slide">
            <div class="subscribe__banner-item">
              <IconCalendar />
              <div class="subscribe__banner-item-content">
                <div class="subscribe__banner-item-title">{{ t('item_1_title') }}</div>
                <div class="subscribe__banner-item-description">{{ t('item_1_description') }}</div>
              </div>

            </div>
          </div>
          <div class="swiper-slide">
            <div class="subscribe__banner-item">
              <IconTruck />
              <div class="subscribe__banner-item-content">
                <div class="subscribe__banner-item-title">
                  {{ t('item_4_title') }}
                </div>
                <LegalText
                  tag="div"
                  content-class="subscribe__banner-item-description"
                  :text="t('item_4_description')"
                />
              </div>
            </div>
          </div>
          <div class="swiper-slide">
            <div class="subscribe__banner-item">
              <IconWallet />
              <div class="subscribe__banner-item-content">
                <div class="subscribe__banner-item-title">{{ t('item_2_title') }}</div>
                <div class="subscribe__banner-item-description">{{ t('item_2_description') }}</div>
              </div>
              </div>
          </div>
          <div class="swiper-slide">
            <div class="subscribe__banner-item">
              <IconPack />
              <div class="subscribe__banner-item-content">
                <div class="subscribe__banner-item-title">
                  {{ t('item_3_title') }}
                </div>
                <div class="subscribe__banner-item-description">
                 {{t('item_3_description')}}
                </div>
              </div>
            </div>
          </div>
          <div class="swiper-slide">
            <div class="subscribe__banner-item">
              <IconCheck />
              <div class="subscribe__banner-item-content">
                <div class="subscribe__banner-item-title">
                 {{t('item_5_title')}}
                </div>
                <div class="subscribe__banner-item-description">
                  {{ t('item_5_description') }}
                </div>
              </div>
             
            </div>
          </div>
        </div>
        <div class="subscribe__banner-controls">
          <div class="swiper-pagination"></div>
          <button  
            ref="autoplayBtn"
            type="button"
            class="subscribe__banner-button"
            :aria-label="isPaused ? t('buttonAriaLabelStart'): t('buttonAriaLabelStop')"
            :class="{ 'is-paused': isPaused }"
            @click="toggleAutoplay"
            >
            <IconPause />
            <IconPlay />
          </button>
        </div>
        <div class="sr-only swiper-status" role="status" aria-live="polite"></div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, onMounted, onBeforeUnmount, ref, nextTick } from 'vue'

import Swiper from 'swiper'
import { A11y, Autoplay, Pagination } from 'swiper/modules'
import LegalText from '../../js/components/LegalText.vue'
import IconCalendar from '@images/i-subscribe-calendar.svg'
import IconWallet from '@images/i-subscribe-wallet.svg'
import IconPack from '@images/i-subscribe-pack.svg'
import IconTruck from '@images/i-subscribe-truck.svg'
import IconCheck from '@images/i-subscribe-check.svg'
import IconPause from '@images/i-subscribe-pause.svg'
import IconPlay from '@images/i-subscribe-play.svg'


const props = defineProps({
  customer: { type: Object, required: true },
  hasSubscription: { type: Boolean, default: false },
})

const swiperEl = ref(null)

let swiperInstance = null
let lastActiveIndex = 0
const AUTOPLAY_DELAY = 5000
const isPaused = ref(false)

const i18n = {
  uk: { 
    breadcrumb_store_label: 'Інтернет-магазин glo',
    breadcrumb_page_label: 'Підписка glo',
    header: 'Підписка від glo™',
    description: 'Оформлюй послугу, щоб замовляти продукцію за фіксованою ціною на щомісячній основі. Період дії від 3 до 4 місяців',
    has_subscription_description: 'Під час редагування Підписки ти можеш переобрати блоки стіків та змінити дані про доставку та оплату',
    buttonAriaLabelStop: 'Зупинити програвання',
    buttonAriaLabelStart: 'Відновити програвання',
    item_1_title: 'Стіки щомісяця',
    item_1_description: 'Отримуйте від 2 до 5 блоків регулярно за фіксованою ціною від 3 до 4 місяців',
    item_2_title: 'Фіксована ціна',
    item_2_description: 'Ціна на період підписки не змінюється, навіть якщо зросте вартість продукції',
    item_3_title: 'Гнучкі умови',
    item_3_description: 'Обирай формат та наповнення підписки, який підходить тобі',
    item_4_title: 'Безкоштовна доставка',
    item_4_description: 'Безкоштовна доставка@legal(delivery) до найближчого відділення Нової Пошти',
    item_5_title: 'Завжди в наявності',
    item_5_description: 'Гарантуємо наявність обраної продукції на період дії послуги'
  },
  ru: { 
    breadcrumb_store_label: 'Интернет-магазин glo',
    breadcrumb_page_label: 'Подписка glo',
    header: 'Подписка от glo™',
    description: 'Оформи услугу, чтобы заказывать продукцию по фиксированной цене на ежемесячной основе. Срок действия от 3 до 4 месяцев.',
    has_subscription_description: 'При редактировании подписки ты можешь выбрать блоки стиков и изменить данные о доставке и оплате',
    buttonAriaLabelStop: 'Остановить воспроизведение',
    buttonAriaLabelStart: 'Возобновить воспроизведение',
    item_1_title: 'Стики ежемесячно',
    item_1_description: 'Получайте от 2 до 5 блоков регулярно по фиксированной цене от 3 до 4 месяцев',
    item_2_title:'Фиксированная цена',
    item_2_description: 'Цена на период подписки не меняется, даже если вырастет стоимость продукции',
    item_3_title: 'Гибкие условия',
    item_3_description: 'Выбирай формат и наполнение подписки, который подходит тебе',
    item_4_title: 'Бесплатная доставка',
    item_4_description: 'Бесплатная доставка@legal(delivery) в ближайшее отделение Новой Почты',
    item_5_title: 'Всегда в наличии',
    item_5_description: 'Гарантируем наличие выбранной продукции на период действия услуги'
  },
}
const locale = computed(() => window.Shopify?.locale || 'uk')
const t = (key) => i18n[locale.value]?.[key] || key
const swiperA11yMessages = computed(() => {
  const isUk = locale.value === 'uk'

  return {
    enabled: true,
    slideLabelMessage: '',
    paginationBulletMessage: isUk ? 'Перейти до слайду {{index}}' : 'Перейти к слайду {{index}}',
    nextSlideMessage: isUk ? 'Наступний слайд' : 'Следующий слайд',
    prevSlideMessage: isUk ? 'Попередній слайд' : 'Предыдущий слайд',
  }
})

function toggleAutoplay() {
  if (!swiperInstance?.autoplay) return

  if (swiperInstance.autoplay.running) {
    swiperInstance.autoplay.stop()
    isPaused.value = true
  } else {
    swiperInstance.autoplay.start()
    isPaused.value = false
  }
}

function updateSwiperStatus(swiper) {
  const statusEl = swiper.el.querySelector('.swiper-status')
  if (!statusEl) return

  const total = swiper.slides.length

  let perView = swiper.params.slidesPerView
  if (perView === 'auto') perView = swiper.slidesPerViewDynamic()
  if (typeof perView !== 'number' || perView <= 0) perView = 1

  const current = swiper.realIndex + 1

  let message
  if (perView === 1) {
    message =
      window.Shopify?.locale === 'uk'
        ? `Слайд ${current} з ${total}`
        : `Слайд ${current} из ${total}`
  } else {
    const start = current
    const end = Math.min(start + perView - 1, total)
    message =
      window.Shopify?.locale === 'uk'
        ? `Слайди ${start}–${end} з ${total}`
        : `Слайды ${start}–${end} из ${total}`
  }

  statusEl.textContent = message
}

function revealAllWithStagger(swiper) {
  swiper.slides.forEach((slide, i) => {
    const item = slide.querySelector('.subscribe__banner-item')
    if (!item) return
    item.style.transitionDelay = `${i * 80}ms`
    requestAnimationFrame(() => item.classList.add('is-visible'))
  })
}

function isDesktopNow() {
  return window.matchMedia('(min-width: 1024px)').matches
}

onMounted(async () => {
  await nextTick()

  const root = swiperEl.value
  if (!root) return

  swiperInstance = new Swiper(root, {
    modules: [A11y, Autoplay, Pagination],

    spaceBetween: 12,

    slidesPerView: 'auto',

    breakpoints: {
      100: { slidesPerView: 'auto' },
      1024: { slidesPerView: 5 },
    },

    autoplay: isDesktopNow()
      ? false
      : {
          delay: AUTOPLAY_DELAY,
          disableOnInteraction: false,
        },

    pagination: {
      el: root.querySelector('.swiper-pagination'),
      clickable: true,
    },
    a11y: swiperA11yMessages.value,

    on: {
      init(swiper) {
        setTimeout(() => {
          swiper.slides.forEach((slide) => slide.removeAttribute('role'))
        }, 0)

        updateSwiperStatus(swiper)
        lastActiveIndex = swiper.snapIndex

        revealAllWithStagger(swiper)
      },

      slideChange(swiper) {
        updateSwiperStatus(swiper)

        const bullets = swiper.pagination?.bullets
        if (bullets?.[lastActiveIndex]) {
          bullets[lastActiveIndex].style.setProperty('--progress', 0)
        }
        lastActiveIndex = swiper.snapIndex
      },

      autoplayTimeLeft(swiper, time, progress) {
        const bullets = swiper.pagination?.bullets
        const activeBullet = bullets?.[swiper.snapIndex]
        if (!activeBullet) return
        activeBullet.style.setProperty('--progress', 1 - progress)
      },
    },
  })
})

onBeforeUnmount(() => {
  if (swiperInstance) {
    swiperInstance.destroy(true, true)
    swiperInstance = null
  }
})
</script>
